import React from 'react';
import { Link } from 'react-router-dom';
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { NavReconClientAuditStatus } from '../../../../../api/rs-hg/nav-recon/types';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { cn } from '../../../../../utilities/cn';
import { mapStatusToColor } from '../../../../../utilities/colorUtilities';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import GenericStatusCell from '../../../../tables/GenericStatusCell';

export interface NavReconOverviewMetricStatusCellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  auditStatus: NavReconClientAuditStatus;
  auditClientComment: string | null;
  value: string | undefined | null;
  linkToPage?: string;
  height?: string;
  fontSize?: string;
  /** Threshold-breach notes shown under a shared "Be aware" callout. */
  thresholdWarnings?: string[];
}

const tooltipSlotClassName =
  'z-[1300] max-w-[min(20rem,calc(100vw-2rem))] rounded-md border border-gray-200 bg-white px-3.5 py-3 text-left text-gray-800 shadow-lg ring-1 ring-black/5';

const tooltipSurfaceColor = '#fff';
const tooltipBorderColor = '#e5e7eb';

/**
 * Renders Pass/Alert/Fail-style metric status. When the row is **manually
 * approved** (`auditStatus === 'approved'`), the chip uses Pass (green) for
 * the bar even if the metric is Alert/Fail. **Exception** styling: a **3px
 * border in the metric’s status colour** (Alert vs Fail, etc.) and label
 * **`{metric} (Approved)`** when the metric is not a true Pass. The tooltip
 * uses the app's card-like white surface and main-blue accent. It appears
 * for approval comments (when the metric was Alert or Fail) and for any
 * threshold warnings passed in `thresholdWarnings`.
 */
const NavReconOverviewMetricStatusCell: React.FC<
  NavReconOverviewMetricStatusCellProps
> = ({
  auditStatus,
  auditClientComment,
  value,
  linkToPage,
  height = '2rem',
  fontSize = '0.8rem',
  thresholdWarnings = [],
  className,
  ...props
}) => {
  const auditApproved = auditStatus === 'approved';
  const rawStatus = value ? specifyStatus(value) : Status.NA;
  const statusForColor = auditApproved ? Status.Pass : rawStatus;
  const trimmed = auditClientComment?.trim() ?? '';
  const showOverrideVisual =
    Boolean(value) &&
    auditApproved &&
    rawStatus !== Status.Pass &&
    rawStatus !== Status.alteredPosition;

  const showApprovalTooltip =
    Boolean(value) &&
    auditApproved &&
    (rawStatus === Status.Fail ||
      rawStatus === Status.Alert ||
      rawStatus === Status.PassAlert);
  const warningItems = thresholdWarnings.filter(
    (warning) => warning.trim().length > 0,
  );
  const showThresholdWarnings = warningItems.length > 0;
  const showTooltip = showApprovalTooltip || showThresholdWarnings;

  const labelText = showOverrideVisual ? `${value} (Approved)` : (value ?? undefined);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const arrowRef = React.useRef<SVGSVGElement | null>(null);
  const { refs, floatingStyles, context } = useFloating({
    open: showTooltip && tooltipOpen,
    onOpenChange: setTooltipOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  });
  const hover = useHover(context, {
    enabled: showTooltip,
    delay: { open: 200, close: 0 },
    move: false,
  });
  const focus = useFocus(context, { enabled: showTooltip });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    role,
  ]);

  if (!value && !showThresholdWarnings) {
    return (
      <div className={cn('text-gray-500', className)} {...props}>
        -
      </div>
    );
  }

  const approvalTooltip = showApprovalTooltip ? (
    trimmed ? (
      <div className="space-y-2">
        <p className="m-0 text-xs font-semibold uppercase tracking-wide text-mainblue">
          Approval Reason
        </p>
        <p className="m-0 whitespace-pre-wrap border-t border-gray-300 pt-2 text-sm font-normal leading-snug text-gray-800">
          {trimmed}
        </p>
      </div>
    ) : (
      <p className="m-0 text-sm leading-snug text-gray-600">
        Approved. No approval comment was recorded.
      </p>
    )
  ) : null;

  const thresholdWarningsTooltip = showThresholdWarnings ? (
    <div className="space-y-2">
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-alert_dark">
        Be aware
      </p>
      {warningItems.length === 1 ? (
        <p className="m-0 border-t border-gray-300 pt-2 text-sm font-normal leading-snug text-gray-800">
          {warningItems[0]}
        </p>
      ) : (
        <ul className="m-0 list-disc space-y-1 border-t border-gray-300 pt-2 pl-4 text-sm font-normal leading-snug text-gray-800">
          {warningItems.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  const tooltipTitle =
    approvalTooltip || thresholdWarningsTooltip ? (
      <div className="space-y-3">
        {approvalTooltip}
        {thresholdWarningsTooltip}
      </div>
    ) : null;

  const statusCell = (
    <GenericStatusCell
      status={statusForColor}
      innerText={labelText}
      height={height}
      fontSize={fontSize}
    />
  );

  const inner = (
    <div
      className={cn(
        'overflow-hidden rounded-md',
        showOverrideVisual &&
          'rounded-[calc(0.375rem_-_3px_-_0.125rem)]',
      )}
    >
      {statusCell}
    </div>
  );

  const chip = showOverrideVisual ? (
    <div
      className={cn(
        'box-border rounded-md border-[3px] border-solid p-0.5',
        showTooltip && 'cursor-help',
        linkToPage && 'cursor-pointer',
      )}
      style={{ borderColor: mapStatusToColor(rawStatus) }}
    >
      {inner}
    </div>
  ) : (
    inner
  );

  const cellContent = value ? (
    chip
  ) : (
    <span className="text-gray-500">-</span>
  );

  const wrapWithRoot = (node: React.ReactNode) => (
    <div className={cn('relative block w-full', className)} {...props}>
      {node}
    </div>
  );

  const wrapWithLink = (node: React.ReactNode) =>
    linkToPage ? (
      <Link
        to={linkToPage}
        className="block w-full no-underline hover:opacity-90"
      >
        {node}
      </Link>
    ) : (
      node
    );

  const thresholdWarningMarker = showThresholdWarnings ? (
    <span
      className="pointer-events-none absolute -right-1 -top-1 z-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-alert shadow-sm ring-1 ring-alert/50"
      aria-hidden
    >
      <HiExclamationTriangle className="h-4 w-4" />
    </span>
  ) : null;

  if (showTooltip && tooltipTitle) {
    return wrapWithRoot(
      wrapWithLink(
        <>
          <div
            ref={refs.setReference}
            {...getReferenceProps({
              className: cn(
                'relative block w-full outline-none',
                linkToPage ? 'cursor-pointer' : 'cursor-help',
              ),
              tabIndex: 0,
            })}
          >
            {cellContent}
            {thresholdWarningMarker}
          </div>
          {tooltipOpen && (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps({ className: tooltipSlotClassName })}
              >
                {tooltipTitle}
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  fill={tooltipSurfaceColor}
                  stroke={tooltipBorderColor}
                  strokeWidth={1}
                />
              </div>
            </FloatingPortal>
          )}
        </>,
      ),
    );
  }

  return wrapWithRoot(wrapWithLink(cellContent));
};

export default NavReconOverviewMetricStatusCell;
